import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";

import { getFileUrl } from "../../../core/config/api.js";
import { SettingsContext } from "../../../contexts/index.jsx";
import ProductSearchForm from "../../../forms/ProductSearch.jsx";
import ProductPreview from "../ProductPreview";
import QuickAddProduct from "../QuickAddProduct";
import StatePanel from "../../../components/ui/StatePanel";
import { Button } from "../../../components/ui";
import SectionTrigger from "../../../forms/SectionTrigger.jsx";
import { requireResponse } from "../../../core/errors/requireResponse";
import "./rc-product-selector.scss";

// ─── RC Product Selector — shared "search & pick a product" widget ─────────
// Lifted verbatim out of forms/RCListingForm.jsx (Phase 1 of the Buy Alerts
// RC-theme redesign — see BUY_ALERTS_IMPLEMENTATION_PLAN.md). No behaviour
// change from the original inline version: same fetch, same markup, same
// classes (rc-product-selector.scss duplicates — not moves — the CSS the
// Listing panel already ships, so /product/:id/edit/product is untouched).
//
// Consumers: forms/RCListingForm.jsx (Listing create/edit) and, from Phase 5
// onward, buyAlerts' RC create/edit flow.

const IImage = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.3"/>
    <circle cx="5.5" cy="6.5" r="1" fill="currentColor"/>
    <path d="M2 10.5L5 7.5L7.5 10L10 8L14 11.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ISearch = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M10 10L13.5 13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

export const RCProductList = ({ filters, onSelect, onResultCount }) => {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset]   = useState(0);
  // The search failed (5xx / dropped connection / 4xx). Distinct from "no
  // products matched": that one invites the rep to add the product, and a
  // failed search must not, or duplicate products get created.
  const [failed, setFailed]   = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const LIMIT = 10;
  const history = useHistory();
  const settings = useContext(SettingsContext);

  // Round 8 audit: clicking the product image opens the global ProductPreview
  // modal (mounted alongside this list via <ProductPreview />). The modal
  // listens for `?preview=<id>` in the URL, so set that param on click. Stop
  // propagation so the click doesn't also fire the card's underlying handlers.
  const onImageClick = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const params = new URLSearchParams(history.location.search);
    params.set("preview", product.id);
    if (settings?.iframeQueryParams) {
      const iframeParams = new URLSearchParams(settings.iframeQueryParams);
      iframeParams.forEach((v, k) => { if (!params.has(k)) params.set(k, v); });
    }
    history.push(`${history.location.pathname}?${params.toString()}`);
  };

  // Reset + fetch when filters change
  useEffect(() => {
    if (!filters) { setItems([]); return; }
    setLoading(true);
    setFailed(false);
    setOffset(0);
    axios.post("/api/products/search", {
      list: "default",
      query: filters.query || "",
      filters,
      paging: { offset: 0, limit: LIMIT },
    }).then((res) => {
      const data = requireResponse(res).data;
      setItems(data?.items ?? []);
      setHasMore(data?.hasMore ?? false);
    }).catch(() => {
      setItems([]);
      setHasMore(false);
      setFailed(true);
    })
      .finally(() => setLoading(false));
  }, [JSON.stringify(filters), retryKey]);

  // Tell the parent how the search went, so it can decide how prominently to
  // offer "add it yourself". `null` while a search is in flight — an empty
  // list mid-fetch is not the same thing as "nothing matched".
  useEffect(() => {
    // A failed search reports null (unknown), never 0 ("nothing matched").
    onResultCount?.(loading || failed ? null : items.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, failed, items.length]);

  const loadMore = () => {
    const next = offset + LIMIT;
    axios.post("/api/products/search", {
      list: "default",
      query: filters?.query || "",
      filters,
      paging: { offset: next, limit: LIMIT },
    }).then((res) => {
      // A swallowed 5xx resolves undefined: keep the list and the Load more
      // button as they are so the rep can simply click again.
      if (!res) return;
      const data = res?.data;
      setItems((prev) => [...prev, ...(data?.items ?? [])]);
      setHasMore(data?.hasMore ?? false);
      setOffset(next);
    }).catch(() => {});
  };

  if (loading) {
    return (
      <div className="rc-lp__plist-loading">
        <span className="rc-lp__plist-dot" /><span className="rc-lp__plist-dot" /><span className="rc-lp__plist-dot" />
      </div>
    );
  }

  if (failed) {
    return (
      <StatePanel
        state="error"
        compact
        title="Couldn't search products"
        description="Something went wrong on our side — try again."
        onRetry={() => setRetryKey((k) => k + 1)}
      />
    );
  }

  if (!loading && items.length === 0) {
    return <div className="rc-lp__plist-empty">No products found</div>;
  }

  return (
    <div className="rc-lp__plist">
      {items.map((product) => {
        const imageUrl  = product.image_id > 0 ? getFileUrl(product.image_id) : null;
        const metaParts = [product.reference_number, product.brand_name].filter(Boolean);
        return (
          <div key={product.id} className="rc-lp__plist-item">
            <div className="rc-lp__plist-meta">
              {metaParts.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="rc-lp__plist-bullet">•</span>}
                  <span>{p}</span>
                </React.Fragment>
              ))}
            </div>
            <div className="rc-lp__plist-name">{product.model_name}</div>
            <div
              className="rc-lp__plist-image rc-lp__plist-image--clickable"
              role="button"
              tabIndex={0}
              onClick={(e) => onImageClick(e, product)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onImageClick(e, product); }}
              title="Open product preview"
            >
              {imageUrl
                ? <img src={imageUrl} alt={product.model_name} />
                : <div className="rc-lp__plist-noimage"><IImage /></div>
              }
            </div>
            <Button
              variant="secondary"
              size="sm"
              fullWidth
              aria-label={`Select ${[product.model_name, product.reference_number].filter(Boolean).join(" ") || "product"}`}
              data-btn="product-confirm"
              onClick={() => onSelect(product)}
            >
              Select
            </Button>
          </div>
        );
      })}
      {hasMore && (
        <Button variant="ghost" size="sm" data-btn="product-search" onClick={loadMore}>
          Load more
        </Button>
      )}
    </div>
  );
};

/**
 * RCProductSelector — collapsible "search & pick a product" section.
 *
 * @param {object} [product] — currently selected product, if any
 * @param {(product: object) => void} onProductSelected
 * @param {string} [labelSelect="Select Product"] — trigger label when no product is selected
 * @param {string} [labelSelected="Product"] — trigger label once a product is selected
 * @param {boolean} [defaultOpen] — initial open state; defaults to `!product?.id`
 * @param {{nonce:number}} [openRequest] — ask the panel to open from outside. Keyed on
 *   the nonce so the same request can be repeated, and so a re-render alone never
 *   reopens a panel the rep has collapsed.
 *   (open when nothing is selected yet, collapsed once something is)
 */
const RCProductSelector = ({
  product,
  onProductSelected,
  labelSelect = "Select Product",
  labelSelected = "Product",
  defaultOpen,
  openRequest,
}) => {
  const [open, setOpen] = useState(defaultOpen ?? !product?.id);
  const [filters, setFilters] = useState(null);
  // null = no search has resolved yet (either none run, or one is in flight).
  const [resultCount, setResultCount] = useState(null);

  // An outside caller (a purpose-built flow's Continue) asking the panel to
  // open, because the next thing the rep must do is pick a product. Keyed on
  // the nonce, so re-requesting works and a plain re-render never reopens a
  // panel the rep deliberately collapsed.
  useEffect(() => {
    if (openRequest?.nonce) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest?.nonce]);

  // Collapse when a product arrives from OUTSIDE this component — the
  // purpose-built flows auto-pick one (from a Sell Request, or a Gemini match),
  // and `open` is seeded by useState at mount only, so the panel stayed
  // expanded on its empty search form even though a product was chosen.
  // handleSelect covers the manual pick; this covers the external one.
  // Collapse only, never auto-open: a rep who deliberately reopened the panel
  // to swap products keeps it open.
  const prevProductIdRef = useRef(product?.id ?? null);
  useEffect(() => {
    const id = product?.id ?? null;
    if (id && id !== prevProductIdRef.current) setOpen(false);
    prevProductIdRef.current = id;
  }, [product?.id]);

  const handleSelect = (p) => {
    if (!p?.id) return;
    // Fetch full product to get EAV values (case_material, dial_color, etc.)
    // needed for auto-title generation
    axios.get(`/api/products/${p.id}`)
      .then((res) => onProductSelected(res.data ?? p))
      .catch(() => onProductSelected(p))
      .finally(() => {
        setOpen(false);
        setFilters(null);
      });
  };

  const imageUrl = product?.images?.[0]
    ? getFileUrl(product.images[0].id)
    : product?.image_id > 0
      ? getFileUrl(product.image_id)
      : null;
  const brandName = product?.brand?.name ?? product?.brand_name ?? "";
  const modelName = product?.name ?? product?.model?.name ?? product?.model_name ?? "";
  const refNumber = product?.reference_number ?? "";

  return (
    <div className="rc-lp__section rc-lp__section--product">
      <SectionTrigger
        icon={<ISearch />}
        label={product?.id ? labelSelected : labelSelect}
        isOpen={open}
        onToggle={() => setOpen((o) => !o)}
        summary={
          product?.id ? (
            <span className="rc-lp__product-chip">
              {imageUrl && <img src={imageUrl} alt="" className="rc-lp__product-chip-img" />}
              <span className="rc-lp__product-chip-name">
                {[brandName, modelName, refNumber].filter(Boolean).join(" · ")}
              </span>
            </span>
          ) : null
        }
      />

      {open && (
        <div className="rc-lp__section-body rc-lp__section-body--product">
          <div className="rc-lp__product-search">
            <ProductSearchForm
              product={product}
              submitAction={(f) => setFilters(f)}
              filters={filters}
            />
          </div>
          {filters && (
            <RCProductList
              filters={filters}
              onSelect={handleSelect}
              onResultCount={setResultCount}
            />
          )}
          {/*
            Add-it-yourself, in the same three states as the request-flow list:
            prominent before any search has run and when a search found nothing,
            a quiet link under a list of results. Renders nothing at all unless
            RC has auto-approval on — QuickAddProduct decides that for itself.
          */}
          <QuickAddProduct
            variant={!filters || resultCount === 0 ? "prominent" : "quiet"}
            prefill={{
              category_id: filters?.category_id,
              brand_id: filters?.brand_id,
              reference_number: filters?.reference_number || filters?.query || "",
            }}
            onCreated={handleSelect}
          />
          {/*
            Round 8 audit: mount ProductPreview so clicking the image of a
            product card opens the popup. ProductPreview reads `?preview=<id>`
            from the URL — RCProductList's onImageClick sets that param.
          */}
          <ProductPreview onSelect={onProductSelected} />
        </div>
      )}
    </div>
  );
};

export default RCProductSelector;
